import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, Twitter, DollarSign, ExternalLink } from 'lucide-react';
import { useTemplate } from '@/context/TemplateContext';
import DynamicIcon from '@/components/common/DynamicIcon';
import { NewsletterForm } from '@/components/forms/NewsletterForm';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import hydraLogo from '@/assets/hydra-logo.png';
import ciWebGroupLogo from '@/assets/ci-web-group-logo-horizontal-white-rgb.svg';

const footerLinks = {
  services: [
    { label: 'Our Services', href: '/services' },
    { label: 'Service Areas', href: '/service-areas' },
    { label: 'Get a Quote', href: '/contact' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Resources', href: '/resources' },
    { label: 'Contact', href: '/contact' },
    { label: 'Careers', href: '/careers' },
    { label: 'Events', href: '/events' },
  ],
  resources: [
    { label: 'Blog', href: '/resources' },
    { label: 'FAQs', href: '/services#faq' },
    { label: 'Financing', href: '/contact' },
  ],
};

export const Footer: React.FC = () => {
  const { template } = useTemplate();
  const { settings: integrationSettings } = useIntegrationSettings();
  const currentYear = new Date().getFullYear();

  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('logo_url, company_name, phone, email, address_street, address_city, address_state, address_zip, business_hours, license_number, facebook_url, instagram_url, linkedin_url, twitter_url')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data: gbpLocations = [] } = useQuery({
    queryKey: ['business-locations-footer'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_locations')
        .select('name, address_street, address_city, address_state, address_zip, phone, google_url, is_primary')
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('sort_order');
      return data || [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const logoUrl = bizSettings?.logo_url;
  const companyName = bizSettings?.company_name || template.brand.name;
  const phone = bizSettings?.phone || template.contact.phone;
  const email = bizSettings?.email || template.contact.email;
  const address = bizSettings?.address_street || template.contact.address;
  const city = bizSettings?.address_city || template.contact.city;
  const state = bizSettings?.address_state || template.contact.state;
  const zip = bizSettings?.address_zip || template.contact.zip;
  const hours = bizSettings?.business_hours || template.contact.hours;
  const license = bizSettings?.license_number || template.contact.license;

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-10 w-auto object-contain" />
              ) : (
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-bold">
                    {companyName.charAt(0)}
                  </span>
                </div>
              )}
              <span className="font-heading font-bold text-lg">{companyName}</span>
            </Link>
            <p className="text-sm text-secondary-foreground/80 mb-4">
              {template.brand.tagline}
            </p>
            <div className="space-y-2 text-sm">
              <a 
                href={`tel:${phone.replace(/\D/g, '')}`}
                className="flex items-center gap-2 hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4" />
                {phone}
              </a>
              <a 
                href={`mailto:${email}`}
                className="flex items-center gap-2 hover:text-primary transition-colors"
              >
                <Mail className="h-4 w-4" />
                {email}
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {address}<br />
                  {city}, {state} {zip}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {hours}
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Services</h4>
            <ul className="space-y-2">
              {template.services.slice(0, 6).map((service) => (
                <li key={service.slug}>
                  <Link 
                    to={`/services#${service.slug}`}
                    className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors flex items-center gap-2"
                  >
                    <DynamicIcon name={service.icon} className="h-3 w-3" />
                    {service.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/services"
                  className="text-sm text-primary hover:underline"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Service Areas */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Stay Updated</h4>
            <p className="text-sm text-secondary-foreground/80 mb-4">
              Get tips, deals, and news delivered to your inbox.
            </p>
            <NewsletterForm variant="stacked" source="footer" />
            
            {/* Locations & Service Areas */}
            {gbpLocations.length > 0 ? (
              <>
                <h4 className="font-heading font-semibold text-lg mb-3">Our Locations</h4>
                <div className="space-y-4">
                  {gbpLocations.map((loc: any, idx: number) => (
                    <div key={idx} className="text-sm space-y-1">
                      <p className="font-medium text-secondary-foreground/90">{loc.name}</p>
                      <div className="flex items-start gap-1.5 text-secondary-foreground/70">
                        <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>{[loc.address_street, `${loc.address_city}, ${loc.address_state} ${loc.address_zip}`].filter(Boolean).join(', ')}</span>
                      </div>
                      {loc.phone && (
                        <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="flex items-center gap-1.5 text-secondary-foreground/70 hover:text-primary transition-colors">
                          <Phone className="h-3.5 w-3.5" /> {loc.phone}
                        </a>
                      )}
                      {loc.google_url && (
                        <a href={loc.google_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          <ExternalLink className="h-3 w-3" /> View on Google
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h4 className="font-heading font-semibold text-lg mb-3">Service Areas</h4>
                <ul className="space-y-1">
                  {template.serviceAreas.slice(0, 4).map((area) => (
                    <li key={area.name}>
                      <Link 
                        to={`/service-areas#${area.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                      >
                        {area.name}
                      </Link>
                    </li>
                  ))}
                  {template.serviceAreas.length > 4 && (
                    <li>
                      <Link 
                        to="/service-areas"
                        className="text-sm text-primary hover:underline"
                      >
                        + {template.serviceAreas.length - 4} more
                      </Link>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Trust Badges & Financing CTA */}
        <div className="mt-8 pt-8 border-t border-secondary-foreground/10">
          <div className="flex flex-wrap justify-center items-center gap-6">
            {template.trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-secondary-foreground/80">
                <DynamicIcon name={badge.icon} className="h-5 w-5 text-primary" />
                <span>{badge.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm text-secondary-foreground/80">
              <span>{license}</span>
            </div>
            {integrationSettings.financing_enabled && integrationSettings.financing_url && (
              <a
                href={integrationSettings.financing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                <DollarSign className="h-4 w-4" />
                {integrationSettings.financing_cta_text || 'Apply for Financing'}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-secondary-foreground text-center md:text-left">
              © {currentYear} {companyName}. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {bizSettings?.facebook_url && (
                <a href={bizSettings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground hover:text-primary transition-colors" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {bizSettings?.instagram_url && (
                <a href={bizSettings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground hover:text-primary transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {bizSettings?.linkedin_url && (
                <a href={bizSettings.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground hover:text-primary transition-colors" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {bizSettings?.twitter_url && (
                <a href={bizSettings.twitter_url} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground hover:text-primary transition-colors" aria-label="Twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-secondary-foreground">
              <Link to="/sitemap" className="hover:text-primary transition-colors">Sitemap</Link>
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hydra V1 Branding Bar */}
      <div className="bg-black border-t border-white/10">
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4">
            {/* Left: High-tech CTA Button */}
            <a 
              href="https://getstarted.ciwebgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-4 py-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white text-xs sm:text-sm font-bold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:scale-105 whitespace-nowrap flex-shrink-0"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="hidden sm:inline">Want a website like this?</span>
                <span className="sm:hidden">Get a Website</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
            
            {/* Center/Right: CI Web Group + Hydra Logos */}
            <div className="flex items-center gap-3 sm:gap-4 ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-xs whitespace-nowrap hidden md:inline">Powered by</span>
                <a 
                  href="https://ciwebgroup.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img 
                    src={ciWebGroupLogo} 
                    alt="AI Website and AI Marketing Agency | Hydra Operating System" 
                    className="h-4 sm:h-5 w-auto"
                  />
                </a>
              </div>
              <div className="w-px h-6 sm:h-8 bg-white/20" />
              <img 
                src={hydraLogo} 
                alt="Hydra OS" 
                className="h-8 sm:h-10 w-auto flex-shrink-0"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
